import { useAuth } from 'react-oidc-context'
import { env } from '@/env'
import { displayNameFromEmail } from '@/lib/displayName'

type Session = {
  isAuthenticated: boolean
  isLoading: boolean
  email: string | null
  /**
   * Best-effort human-readable name. Priority chain:
   *   1. `profile.name`
   *   2. `profile.given_name` (+ optional `family_name`)
   *   3. `profile.preferred_username`
   *   4. Email-derived name from `profile.email`
   *   5. `null` — caller should fall back to the raw email label
   *
   * Source-of-truth is the ID token (merged with `/userinfo` if ever
   * re-enabled via `loadUserInfo`). Any future extension — Microsoft's
   * `upn`, SAML mapped claims, etc. — slots into this function.
   */
  displayName: string | null
  sub: string | null
  accessToken: string | null
  signin: () => Promise<void>
  signout: () => Promise<void>
}

/** Read a string-ish profile claim. Coerces numeric `sub` (Doorkeeper
 *  sometimes emits integer IDs) to string for downstream query keys. */
function profileString(
  profile: Record<string, unknown> | undefined,
  key: string,
): string | null {
  if (!profile) return null
  const v = profile[key]
  if (typeof v === 'string' && v.length > 0) return v
  if (typeof v === 'number') return String(v)
  return null
}

function resolveDisplayName(
  profile: Record<string, unknown> | undefined,
  email: string | null,
): string | null {
  const name = profileString(profile, 'name')
  if (name) return name
  const given = profileString(profile, 'given_name') ?? ''
  const family = profileString(profile, 'family_name') ?? ''
  if (given || family) return [given, family].filter(Boolean).join(' ')
  const preferred = profileString(profile, 'preferred_username')
  if (preferred) return preferred
  return displayNameFromEmail(email)
}

/**
 * Thin wrapper over `react-oidc-context`'s `useAuth()` that normalises
 * identity into a single source-of-truth object for the rest of the
 * Lobby. All fields derive from `auth.user.profile` (the ID-token
 * claim set). Passport must include `email`, `name` etc. in the ID
 * token when the `email` / `profile` scopes are granted; without
 * those, `displayName` falls back to an email-derived best-effort.
 */
export function useSession(): Session {
  const auth = useAuth()
  const profile = auth.user?.profile as Record<string, unknown> | undefined
  const email = profileString(profile, 'email')

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    email,
    displayName: resolveDisplayName(profile, email),
    sub: profileString(profile, 'sub'),
    accessToken: auth.user?.access_token ?? null,
    signin: () => auth.signinRedirect(),
    signout: () =>
      auth.signoutRedirect({
        post_logout_redirect_uri: `${env.VITE_LOBBY_ORIGIN}/signed-out`,
      }),
  }
}
